import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, CheckIcon, RouteIcon, SparklesIcon, PencilIcon, SplitIcon } from 'lucide-react';
import type { DestinationId, VendorRouting } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { BONZO_CAMPAIGNS, FIVE9_LISTS } from '../../lib/mockData';
import { SearchableSelect, type Option } from '../ui/SearchableSelect';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  onToggle: (id: DestinationId) => void;
  routing: VendorRouting[];
  onRoutingChange: (routing: VendorRouting[]) => void;
  onBack: () => void;
  onContinue: () => void;
}
export function Step3Destinations({
  selected,
  onToggle,
  routing,
  onRoutingChange,
  onBack,
  onContinue
}: Props) {
  const bonzoOptions: Option[] = useMemo(() => BONZO_CAMPAIGNS.map(c => ({
    value: c.id,
    label: c.name,
    secondary: c.id,
    description: c.description
  })), []);
  const five9Options: Option[] = useMemo(() => FIVE9_LISTS.map(l => ({
    value: l.id,
    label: l.name,
    secondary: l.id
  })), []);
  const bonzoSelected = selected.includes('bonzo');
  const five9Selected = selected.includes('five9');
  const multiVendor = routing.length > 1;
  function setBonzo(vendor: string, campaignId: string) {
    onRoutingChange(routing.map(r => r.vendor === vendor ? {
      ...r,
      bonzoCampaignId: campaignId,
      autoSelected: false
    } : r));
  }
  function setFive9(vendor: string, listId: string) {
    onRoutingChange(routing.map(r => r.vendor === vendor ? {
      ...r,
      five9ListId: listId,
      autoSelected: false
    } : r));
  }
  const canContinue = selected.length > 0 && (!bonzoSelected || routing.every(r => r.bonzoCampaignId)) && (!five9Selected || routing.every(r => r.five9ListId));
  return <div className="space-y-6" data-id="element-145" data-mp="1wocizsd95z0k">
      {/* Destination selection */}
      <section data-id="element-146" data-mp="2anc7n00rmdwq">
        <h3 className="mb-1 text-sm font-semibold text-gray-900" data-id="element-147" data-mp="2anu8yb2abibi">
          Select destinations
        </h3>
        <p className="mb-4 text-sm text-gray-500" data-id="element-148" data-mp="26je5egg58mzg">
          Choose one or more places to send this batch of leads.
        </p>
        <div className="grid gap-4 sm:grid-cols-3" data-id="element-149" data-mp="2fldmf8r5rr2e">
          {DESTINATIONS.map(d => {
          const isOn = selected.includes(d.id);
          return <button key={d.id} type="button" onClick={() => onToggle(d.id)} aria-pressed={isOn} className={cn('relative flex flex-col items-start gap-3 rounded-2xl border-2 bg-white p-5 text-left transition-all', isOn ? 'border-salesforce-500 shadow-card-lg ring-2 ring-salesforce-500/10' : 'border-gray-200 shadow-card hover:border-gray-300')} data-id="element-150" data-mp="2u9guqosobeud">
                <span className={cn('flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors', 'absolute right-4 top-4', isOn ? 'border-salesforce-500 bg-salesforce-500 text-white' : 'border-gray-300 bg-white')} data-id="element-151" data-mp="38x8tpmybd2sa">
                  {isOn && <CheckIcon className="h-3.5 w-3.5" data-id="element-152" data-mp="2p9hvu3zvh0y2" />}
                </span>
                <img src={d.icon} alt="" className="h-12 w-12 rounded-xl" data-id="element-153" data-mp="2p756v6vp5qbi" />
                <div data-id="element-154" data-mp="2u8cbix2jm9dy">
                  <p className="text-base font-semibold text-gray-900" data-id="element-155" data-mp="2g8bbtky0l802">
                    {d.name}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-156" data-mp="2l7w9ls1g7ab7">
                    {d.description}
                  </p>
                </div>
              </button>;
        })}
        </div>
      </section>

      {/* Vendor routing summary */}
      <AnimatePresence initial={false} data-id="element-157" data-mp="288dmgjzgy9te">
        {(bonzoSelected || five9Selected) && <motion.section initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-visible" data-id="element-158" data-mp="23f2g0jykjv20">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-card" data-id="element-159" data-mp="1khhpz9k2iqft">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3.5" data-id="element-160" data-mp="1ggfriuxz6iew">
                <div className="flex items-center gap-2" data-id="element-161" data-mp="3ewk8gnap9898">
                  <RouteIcon className="h-4 w-4 text-salesforce-600" data-id="element-162" data-mp="3ety1opvmuws2" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-163" data-mp="3auyroqv1o9tr">
                    Vendor-based routing
                  </h3>
                </div>
                {multiVendor && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700" data-id="element-164" data-mp="11khja1wjg91q">
                    <SplitIcon className="h-3.5 w-3.5" data-id="element-165" data-mp="16evb5ubzry1n" />
                    {routing.length} vendors detected — batch will split into{' '}
                    {routing.length} routing groups
                  </span>}
              </div>

              <div className="divide-y divide-gray-100" data-id="element-166" data-mp="inps76ar8pn4">
                {routing.map(r => <div key={r.vendor} className="px-5 py-4" data-id="element-167" data-mp="mnqibo77upa0">
                    <div className="mb-3 flex flex-wrap items-center gap-2" data-id="element-168" data-mp="rncxi36luflg">
                      <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-800" data-id="element-169" data-mp="rl8j2rb2xy6x">
                        {r.vendor}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-170" data-mp="230x59ggiet6o">
                        {formatNumber(r.rowCount)} rows
                      </span>
                      {r.autoSelected ? <span className="inline-flex items-center gap-1 rounded-full bg-salesforce-50 px-2 py-0.5 text-[11px] font-medium text-salesforce-700" data-id="element-171" data-mp="3v3rqbibx0h1b">
                          <SparklesIcon className="h-3 w-3" data-id="element-172" data-mp="7z3kbhn7xmlj" />
                          Auto-selected
                        </span> : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-id="element-173" data-mp="3gdc6o7z5dh59">
                          <PencilIcon className="h-3 w-3" data-id="element-174" data-mp="3q5hxvfhkruje" />
                          Manually overridden
                        </span>}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2" data-id="element-175" data-mp="2xodu7tzz0wlo">
                      {bonzoSelected && <div data-id="element-176" data-mp="2sqm517qpnol6">
                          <label className="mb-1 block text-xs font-medium text-gray-500" data-id="element-177" data-mp="2spb034lj4ash">
                            Bonzo campaign
                          </label>
                          <SearchableSelect options={bonzoOptions} value={r.bonzoCampaignId} onChange={v => setBonzo(r.vendor, v)} placeholder="Select campaign…" searchPlaceholder="Search campaigns…" ariaLabel={`Bonzo campaign for ${r.vendor}`} data-id="element-178" data-mp="1cyrxqute1c3q" />
                        </div>}
                      {five9Selected && <div data-id="element-179" data-mp="1hxbod8ixfc2f">
                          <label className="mb-1 block text-xs font-medium text-gray-500" data-id="element-180" data-mp="1mwo7pjala109">
                            Five9 list
                          </label>
                          <SearchableSelect options={five9Options} value={r.five9ListId} onChange={v => setFive9(r.vendor, v)} placeholder="Select list…" searchPlaceholder="Search lists…" ariaLabel={`Five9 list for ${r.vendor}`} data-id="element-181" data-mp="6h4tuodk8cee" />
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.section>}
      </AnimatePresence>

      {/* Footer */}
      <div className="flex items-center justify-between" data-id="element-182" data-mp="1gw8r60nf8b4w">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" data-id="element-183" data-mp="1gyuzi2i4yuo6">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-184" data-mp="24n4sbodroiot" />
          Back to mapping
        </button>
        <button type="button" disabled={!canContinue} onClick={onContinue} className={cn('inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors', canContinue ? 'bg-salesforce-500 hover:bg-salesforce-600' : 'cursor-not-allowed bg-gray-300')} data-id="element-185" data-mp="1zv37vf6g3lnp">
          Review batch
          <ArrowRightIcon className="h-4 w-4" data-id="element-186" data-mp="2m538ofcmshfc" />
        </button>
      </div>
    </div>;
}